import { CommonModule, KeyValuePipe, NgOptimizedImage } from '@angular/common';
import { Component, computed, inject, input, InputSignal, signal, Signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  BasketService,
  ColorService,
  ConfigService,
  GsDropdownMenuItem,
  GsResponsiveDirective,
  ImageService,
  InformationService,
  MyChainstoreService,
  OrderByPipe,
  StyleService,
  TAILWIND_BREAKPOINTS,
  SystemService,
  WatchlistService
} from 'libs/shared/src';
import { GsIconComponent } from 'libs/shared/src/components/gs-icon/gs-icon.component';
import { DropdownComponent } from '../../dropdown/dropdown.component';
import { MegaMenuComponent } from '../../megamenu/megamenu.component';
import { MyChainstoreComponent } from '../../my-chainstore/my-chainstore.component';
import { TypeaheadComponent } from '../../typeahead/typeahead.component';
import { HeaderSidebarComponent } from '../components/header-sidebar/header-sidebar.component';
import { TopbarComponent } from '../components/topbar/topbar.component';
import { TopbarHeightDirective } from '../directives/topbar-height.directive';
import { HeaderService } from '../services/header.service';

/**
 * Viewport width from which the chainstore label fits next to the desktop menu.
 * Below it the menu (rendered from `xxl`) and the action icons already fill the row, so the label
 * would be squeezed down to a few characters.
 */
const CHAINSTORE_LABEL_MIN_WIDTH = 1700;

@Component({
  selector: 'header-slim',
  imports: [
    CommonModule,
    NgOptimizedImage,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    DropdownComponent,
    GsIconComponent,
    MegaMenuComponent,
    MyChainstoreComponent,
    HeaderSidebarComponent,
    TypeaheadComponent,
    OrderByPipe,
    TopbarComponent,
    TopbarHeightDirective,
    GsResponsiveDirective
  ],
  providers: [KeyValuePipe],
  templateUrl: './header-slim.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    :host {
      display: block;
    }
  `
})
export class HeaderSlimComponent {
  // Services
  protected readonly basket = inject(BasketService);
  protected readonly colorService = inject(ColorService);
  protected readonly config = inject(ConfigService);
  protected readonly headerService = inject(HeaderService);
  protected readonly imageService = inject(ImageService);
  protected readonly infoService = inject(InformationService);
  protected readonly myChainstoreService = inject(MyChainstoreService);
  protected readonly styleService = inject(StyleService);
  protected readonly system = inject(SystemService);
  protected readonly watchlist = inject(WatchlistService);

  // Inputs & Models
  /** Scroll position on main page */
  scrollPosition: InputSignal<number> = input.required<number>();

  protected readonly topbarOpen = signal(true);
  /**
   * Whether the chainstore label is shown: below `xxl` there is no desktop menu, above it the row needs the
   * extra width. Stays hidden while the width is unknown (server-side rendering), so the header does not
   * jump once hydration delivers the real value.
   */
  protected readonly showChainstoreLabel: Signal<boolean> = computed(() => {
    const width = this.styleService.viewportWidth();
    if (width <= 0) return false;
    return width < TAILWIND_BREAKPOINTS.xxl || width >= CHAINSTORE_LABEL_MIN_WIDTH;
  });

  toggleTopbar(state?: boolean): boolean {
    state ??= !this.topbarOpen();
    this.topbarOpen.set(state);
    return state;
  }

  /**
   * Handles the click event on a mainbar action.
   * @param name that was clicked
   * @returns true if the action was handled, false otherwise
   */
  handleActionClick(name: string, ev: Event): boolean {
    ev.preventDefault();
    return this.headerService.performAction(name);
  }

  /**
   * Handles the mouse enter event on a dropdown item.
   * @param dropdownComp The dropdown component
   * @param event The mouse event
   * @param menuItem The dropdown item being hovered.
   */
  toggleDropdownOf(dropdownComp: DropdownComponent | MegaMenuComponent, event: MouseEvent, menuItem: GsDropdownMenuItem) {
    if (menuItem.items.length === 0) {
      return;
    }

    const isMouseEnter = event.type === 'mouseenter';
    const isMouseLeave = event.type === 'mouseleave';
    if (isMouseEnter || isMouseLeave) {
      dropdownComp.toggleDropdown(isMouseEnter, event, menuItem, false);
      return;
    }

    dropdownComp.toggleDropdown(undefined, event, menuItem, false);
  }
}
